import React, { useState } from 'react'
import Button from 'components/button'
import Input from 'components/input'
import Popover, { popoverPlacement } from 'components/popover'
import Select from 'components/select'
import Tooltip from 'components/tooltip'
import { AlignCenter, AlignLeft, AlignRight, Bold, Delete, Italic, NoNumbering, Underline } from 'components/icons'
import { IContextMenuState, IHeadingStyle } from 'types'
import { useThemedColours } from 'hooks/useTheme'
import ColourField from 'components/colour-field'
import ToggleGroup from 'views/settings/components/formatting/components/toggleGroup'

// The controls' column is as wide as the widest of them, four 32px toggles 4px apart, which with the
// longest label ("Numbering") fills the popover. About how tall it stands with every setting showing.
const CONTROL_WIDTH = 140
const POPOVER_HEIGHT = 280

// The sizes a heading can take, as a percentage of the body text.
const MIN_SIZE = 10
const MAX_SIZE = 300

const NUMBERING = [
  { value: '0', label: 'Numbers', content: '1.' },
  { value: '1', label: 'Letters', content: 'A.' },
  { value: '2', label: 'Roman numerals', content: 'I.' },
  { value: '3', label: 'No numbering', content: <NoNumbering /> }
]

/** The marker a heading's numbering style puts before it, as the sample shows it. */
const numberingMarker = (numberingStyle: number | undefined, startAt0: boolean | undefined): string => {
  if (numberingStyle === 1) return 'A.'
  if (numberingStyle === 2) return 'I.'
  if (numberingStyle === 3) return ''
  return startAt0 ? '0.' : '1.'
}

/** What the sample doesn't show at a glance: its size, and anything set beyond the plain style. */
const summarise = ({ fontSize, bold, italic, underline, alignment }: IHeadingStyle): string =>
  [
    `${Math.round(fontSize * 100)}%`,
    bold && 'Bold',
    italic && 'Italic',
    underline && 'Underlined',
    alignment === 'center' && 'Centred',
    alignment === 'right' && 'Right-aligned'
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * A label and its control, in the popover's two columns: the labels' as wide as the longest of them,
 * the controls' the rest. Labels take the body weight, so the popover's title leads.
 */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <div className="text-body">{label}</div>
    <div className="flex min-w-0 items-center">{children}</div>
  </>
)

/**
 * The size, as a percentage, in a text field. It saves when the field is left or Enter is pressed,
 * as a size typed into a design tool's panel does; Escape puts it back.
 */
const SizeField: React.FC<{ percent: number; onSave: (percent: number) => void }> = ({ percent, onSave }) => {
  const [text, setText] = useState<string>(String(percent))
  const [shown, setShown] = useState<number>(percent)
  // A size saved elsewhere (or put back after a failed save) replaces what's shown.
  if (shown !== percent) {
    setShown(percent)
    setText(String(percent))
  }
  const commit = () => {
    const value = Math.round(Number(text))
    if (!Number.isFinite(value) || text.trim() === '') {
      setText(String(percent))
      return
    }
    const clamped = Math.min(MAX_SIZE, Math.max(MIN_SIZE, value))
    setText(String(clamped))
    if (clamped !== percent) onSave(clamped)
  }

  return (
    <div className="relative w-full">
      <Input
        aria-label="Size"
        inputMode="numeric"
        className="h-8 rounded-md pr-8 tabular-nums"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit()
          if (event.key === 'Escape' && text !== String(percent)) {
            // Puts the size back, keeping the popover open; a second Escape closes it.
            event.stopPropagation()
            setText(String(percent))
          }
        }}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted">%</span>
    </div>
  )
}

interface HeadingStyleRowProps {
  name: string
  /** What the style is for, under the popover's title: "How document names look." */
  description: string
  formatting: IHeadingStyle
  /** Saves a change; resolves true once it's saved. */
  onUpdate: (formatting: IHeadingStyle) => Promise<boolean>
  /** Removes the style: the deepest heading level only. */
  onRemove?: () => void
  /** Why the style can't be removed, said on its disabled remove button when there's no onRemove. */
  cantRemove?: string
  /** Opens the style's settings straight away: a level just added. */
  defaultOpen?: boolean
}

/**
 * A heading style: a sample of the heading as it looks, and an Edit button that opens its settings
 * in a popover, in the manner of shadcn/ui's (and, for the deepest level, a button to remove it). Each setting saves as it's changed, and the sample
 * follows. Clicking away or pressing Escape closes it.
 */
const HeadingStyleRow: React.FC<HeadingStyleRowProps> = ({
  name,
  description,
  formatting,
  onUpdate,
  onRemove,
  cantRemove,
  defaultOpen
}) => {
  const themed = useThemedColours()
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const [openedByDefault, setOpenedByDefault] = useState(false)
  const { colour, bold, italic, underline, numberingStyle, startAt0, fontSize, alignment } = formatting
  const marker = numberingStyle !== undefined ? numberingMarker(numberingStyle, startAt0) : ''

  // Below Edit (above, near the foot of the window), its right edge lined up with Edit's.
  const open = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    setAt(popoverPlacement(rect, { height: POPOVER_HEIGHT }))
  }
  const close = () => {
    setAt(null)
    trigger?.focus({ preventScroll: true })
  }

  // A level just added opens once its row is on the page.
  if (defaultOpen && !openedByDefault && trigger) {
    setOpenedByDefault(true)
    open(trigger)
  }

  const toggleStyle = (style: string) => {
    if (style === 'bold') onUpdate({ ...formatting, bold: !bold })
    if (style === 'italic') onUpdate({ ...formatting, italic: !italic })
    if (style === 'underline') onUpdate({ ...formatting, underline: !underline })
  }

  return (
    <div className="flex items-center gap-4 mx-4 py-3 text-[13px]" role="group" aria-label={name}>
      <span
        className={`min-w-0 flex-1 truncate leading-tight ${bold ? 'font-bold' : 'font-normal'} ${
          italic ? 'italic' : ''
        } ${underline ? 'underline' : ''}`}
        style={{ color: themed.onSurface(colour), fontSize: `${fontSize * 100}%` }}
      >
        {marker && <span className="mr-2">{marker}</span>}
        {name}
      </span>
      <span className="shrink-0 text-muted">{summarise(formatting)}</span>
      <Button
        innerRef={setTrigger}
        internalType="outline"
        aria-label={`Edit ${name}`}
        aria-haspopup="dialog"
        aria-expanded={at !== null}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => (at ? close() : open(event.currentTarget))}
      >
        Edit
      </Button>
      {/* On every row, so the rows' buttons line up; disabled, saying why, where the style can't go. */}
      <Tooltip content={onRemove ? `Remove ${name}` : cantRemove || `${name} can't be removed`}>
        <span className="inline-flex">
          <Button
            internalType="outline"
            aria-label={`Remove ${name}`}
            className="size-8 p-0"
            disabled={!onRemove}
            onClick={onRemove}
          >
            <Delete />
          </Button>
        </span>
      </Tooltip>

      <Popover
        id="heading-style"
        at={at}
        onClose={close}
        label={`${name} style`}
        title="Style"
        description={description}
      >
        <div
          className="grid items-center gap-x-4 gap-y-2"
          style={{ gridTemplateColumns: `minmax(0, 1fr) ${CONTROL_WIDTH}px` }}
        >
          <Field label="Text">
            <ToggleGroup
              label="Text"
              items={[
                { value: 'bold', label: 'Bold', content: <Bold /> },
                { value: 'italic', label: 'Italic', content: <Italic /> },
                { value: 'underline', label: 'Underline', content: <Underline /> }
              ]}
              pressed={[bold && 'bold', italic && 'italic', underline && 'underline'].filter(
                (style): style is string => !!style
              )}
              onToggle={toggleStyle}
            />
          </Field>

          <Field label="Size">
            <SizeField
              percent={Math.round(fontSize * 100)}
              onSave={(percent) => onUpdate({ ...formatting, fontSize: percent / 100 })}
            />
          </Field>

          <Field label="Colour">
            <ColourField
              label={`${name} colour`}
              value={colour}
              onSave={(hex) => onUpdate({ ...formatting, colour: hex })}
              variant="field"
              align="start"
              nested
              // Saved as it's picked, as everything else in this popover is.
              confirm={false}
            />
          </Field>

          {alignment !== undefined && (
            <Field label="Alignment">
              <ToggleGroup
                label="Alignment"
                items={[
                  { value: 'left', label: 'Align left', content: <AlignLeft /> },
                  { value: 'center', label: 'Align centre', content: <AlignCenter /> },
                  { value: 'right', label: 'Align right', content: <AlignRight /> }
                ]}
                pressed={[alignment || 'left']}
                onToggle={(value) =>
                  onUpdate({ ...formatting, alignment: value === 'center' || value === 'right' ? value : 'left' })
                }
              />
            </Field>
          )}

          {numberingStyle !== undefined && startAt0 !== undefined && (
            <Field label="Numbering">
              <ToggleGroup
                label="Numbering"
                items={NUMBERING}
                pressed={[String(numberingStyle)]}
                onToggle={(value) => onUpdate({ ...formatting, numberingStyle: Number.parseInt(value) })}
              />
            </Field>
          )}

          {numberingStyle === 0 && startAt0 !== undefined && (
            <Field label="Start at">
              <div className="h-8 w-full">
                <Select
                  className="rounded-md py-[5px]"
                  options={[
                    { value: '0', label: '0' },
                    { value: '1', label: '1' }
                  ]}
                  optionsSelected={[startAt0 ? '0' : '1']}
                  onOptionClick={(option) => onUpdate({ ...formatting, startAt0: option === '0' })}
                  searchable={false}
                />
              </div>
            </Field>
          )}
        </div>
      </Popover>
    </div>
  )
}

export default HeadingStyleRow
